import type { AxiosError } from 'axios';

export class ApiError extends Error {
  status?: number;
  code?: string;
  /** The API's own machine-readable `code` from the response body, when it sends one. */
  serverCode?: string;
  requestId?: string;
  endpoint?: string;
  method?: string;
  url?: string;
  // Accept any structured params; we don't index into it here
  params?: unknown;
  responseDataSnippet?: unknown;

  constructor(
    message: string,
    options: {
      cause?: unknown;
      code?: string;
      endpoint?: string;
      method?: string;
      params?: unknown;
      requestId?: string;
      responseDataSnippet?: unknown;
      serverCode?: string;
      status?: number;
      url?: string;
    } = {},
  ) {
    super(message, { cause: options.cause as Error | undefined });
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.serverCode = options.serverCode;
    this.requestId = options.requestId;
    this.endpoint = options.endpoint;
    this.method = options.method;
    this.url = options.url;
    this.params = options.params;
    this.responseDataSnippet = options.responseDataSnippet;
  }
}

/**
 * Converts an unknown error (typically an AxiosError) into a structured ApiError instance.
 *
 * @param error - The error to convert, typically an AxiosError from a failed HTTP request
 * @param context - Additional context for the error
 * @param context.endpoint - The API endpoint/controller method name that was called
 * @param context.params - Optional parameters that were passed to the API call
 * @returns A structured ApiError instance with status, code, request ID, and other metadata extracted from the axios error
 */
export function toApiError(
  error: unknown,
  context: { endpoint: string; params?: unknown },
): ApiError {
  const ax = error as AxiosError<any> | undefined;
  const status = ax?.response?.status;
  const code = ax?.code;
  const requestId = (ax?.response?.headers?.['x-request-id'] ||
    ax?.response?.headers?.['x-requestid']) as string | undefined;
  const method = ax?.config?.method?.toUpperCase();
  const url = ax?.config?.url;

  const data = ax?.response?.data as any;
  const serverMessage =
    (data && (data.message || data.error || data.title)) ||
    ax?.message ||
    'Request failed';

  let snippet: unknown = undefined;

  try {
    if (data) {
      if (typeof data === 'object') {
        const str = JSON.stringify(data);

        snippet = str.length > 2000 ? str.slice(0, 2000) : str;
      } else {
        snippet = String(data).slice(0, 2000);
      }
    }
  } catch (_) {
    // best effort
  }

  return new ApiError(serverMessage, {
    cause: error,
    code,
    endpoint: context.endpoint,
    method,
    params: context.params,
    requestId,
    responseDataSnippet: snippet,
    serverCode: typeof data?.code === 'string' ? data.code : undefined,
    status,
    url,
  });
}
